<template>
  <layout-public>
    <page-meta />

    <page-hero
      :crumbs="[
        { label: t('Home'), href: route('index') },
        { label: t('Services'), href: route('index') + '#services' },
        { label: t('Brand presence') }
      ]"
      :title="t('Brand Presence')"
      :lede="t('A complete logo and colour palette, with every single working file that your team, your printer and your website builder will ever ask you for.')"
    />

    <!-- 1. The three seconds, argued next to the actual kit sheet rather than
         on a row of cards. Copy column left, spec sheet right. -->
    <section class="bp-open">
      <div class="container">
        <div class="bp-open-grid">
          <section-title
            align="left"
            flush
            data-aos="fade-right"
            :kicker="t('Before they read a word.')"
            :title="t('Buyers decide in the first three seconds')"
            :lede="t('Those three seconds are your logo, your colours and your spacing. Done well, a young company instantly reads as an established, trusted one.')"
          >
            <div class="action-row">
              <app-button href="#brand-kit" icon="arrow-down">
                {{ t('See the kit') }}
              </app-button>
            </div>
          </section-title>

          <!-- The sheet is built from type, rules and the real mark. Nothing
               here is a screenshot, so it stays sharp and stays translated. -->
          <div class="bp-sheet" data-aos="fade-left">
            <div class="bp-board">
              <span class="bp-board-label">{{ t('Logo lockups') }}</span>
              <div class="bp-lockups">
                <div class="bp-lockup">
                  <img src="/images/logo/logo.svg" alt="" />
                  <span class="bp-wordmark">VEXIMUS</span>
                </div>
                <div class="bp-lockup bp-lockup--inverse">
                  <img src="/images/logo/logo-white.svg" alt="" />
                  <span class="bp-wordmark">VEXIMUS</span>
                </div>
                <div class="bp-lockup bp-lockup--mark">
                  <img src="/images/logo/logo-white.svg" alt="" />
                </div>
              </div>
            </div>

            <div class="bp-board">
              <span class="bp-board-label">{{ t('Colour palette') }}</span>
              <!-- The deck is SwatchList's, the same stack a portfolio case
                   study shows. The kit lists codes only, which is what a printer
                   or a developer hands over, so no names are passed. -->
              <swatch-list :swatches="palette" />
            </div>

            <div class="bp-board">
              <span class="bp-board-label">{{ t('Type and hierarchy') }}</span>
              <div class="bp-type">
                <span class="bp-aa" aria-hidden="true">Aa</span>
                <ul class="bp-scale">
                  <li v-for="step in typeScale" :key="step.size">
                    <span class="bp-scale-bar" :style="{ width: step.width + '%' }"></span>
                    <span class="bp-scale-size">{{ step.size }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <h3 class="bp-send-title" data-aos="fade-up">{{ t('Bring what you have') }}</h3>
        <ul class="bp-send">
          <li v-for="need in logoNeeds" :key="need.title" data-aos="fade-up">
            <span class="bp-send-icon" aria-hidden="true">
              <font-awesome-icon :icon="need.icon" />
            </span>
            <div>
              <strong>{{ need.title }}</strong>
              <p>{{ need.body }}</p>
            </div>
          </li>
        </ul>
      </div>
    </section>

    <!-- 2. The surfaces. A hairline mosaic of the touchpoints, so "one look" is
         shown rather than described, then three numbered reasons on rules. -->
    <section class="bp-surfaces dark" id="brand-kit">
      <div class="container">
        <section-title
          tone="dark"
          data-aos="fade-up"
          :kicker="t('One look across every channel')"
          :title="t('One kit, every surface.')"
          :lede="t('The brochure, the name card, the website and the social profile picture should never look like they came from four completely different companies.')"
        />

        <!-- A hairline mosaic rather than a row of cards. Six touchpoints with
             deliberately uneven spans, so the wall reads as one composed
             artwork instead of six matching boxes. -->
        <div class="bp-wall">
          <figure
            v-for="(surface, index) in surfaces"
            :key="surface.mod"
            class="bp-tile"
            data-aos="fade-up"
            :class="'bp-tile--' + surface.mod"
          >
            <span class="bp-tile-art">
              <img class="bp-tile-img" :src="surface.src" alt="" loading="lazy" decoding="async" />
            </span>
            <figcaption class="bp-tile-tag">
              <span class="bp-tile-num" aria-hidden="true">{{ String(index + 1).padStart(2, '0') }}</span>{{ surface.label }}
            </figcaption>
          </figure>
        </div>

        <div class="bp-reasons">
          <panel-list
            tone="dark"
            bare
            mark="number"
            :columns="3"
            :items="brandWhy"
          />
        </div>
      </div>
    </section>

    <!-- 3. The delivery list. A spec sheet rather than six more cards: piece,
         what it is for, and the file formats the supplier will ask for. -->
    <section class="bp-manifest">
      <div class="container">
        <section-title
          data-aos="fade-up"
          :kicker="t('The delivery sheet')"
          :title="t('What lands in your hands')"
          :lede="t('Never simply a picture of a logo. These are the working files which your printer, your website builder and your social templates ask you for.')"
        />

        <panel-list
          class="bp-manifest-list"
          mark="number"
          trailing
          still
          bare
          :items="kitItems"
        >
          <!-- Each format is drawn as the file icon a supplier would see in a
               folder, not as a bare label: page shape with the corner folded,
               the family mark in the body, the extension on a colour tab. -->
          <template #extra="{ item }">
            <ul class="bp-formats">
              <li
                v-for="format in item.formats"
                :key="format"
                :class="'is-' + formatFamily(format)"
              >
                <span class="bp-file" aria-hidden="true">
                  <font-awesome-icon :icon="formatIcon(format)" class="bp-file-mark" />
                  <span class="bp-file-ext">{{ format }}</span>
                </span>
              </li>
            </ul>
          </template>
        </panel-list>

        <p class="bp-manifest-note" data-aos="fade-up">{{ t('A brand kit is a one-time cost that every flyer, slide and post after it reuses. It also stops the slow drip of small fixes that make a business look smaller than it is.') }}</p>
      </div>
    </section>

    <!-- 4. The pieces beyond the kit. The promo brief panel's ink slab rather
         than another light copy block: the section argues that you order only
         what you need, so it closes on the same dark moment the promo pages
         use for a checklist.

         The section title sits inside the slab, which is how the promo details
         pages open their equivalent panel (Brief.vue). It used to sit on
         the white ground above the slab while a second, hand-rolled head
         inside it repeated the point in another size ("Order sheet" plus a
         one-line note). That was two titles for one section, and it made the
         slab the only panel on the site opening with a label instead of the
         shared title. -->
    <section class="bp-order">
      <div class="container">
        <ink-slab>
          <section-title
            align="left"
            tone="dark"
            data-aos="fade-up"
            :kicker="t('Next to the website')"
            :title="t('Order only the pieces you need')"
            :lede="t('Beyond the kit, we produce the individual branded pieces when you need them, each piece quoted separately so nothing is ever bundled together.')"
          />

          <panel-list
            tone="dark"
            bare
            icon="check"
            mark-size="sm"
            mark-variant="outline"
            :columns="2"
            :items="graphicPieces"
          />
        </ink-slab>
      </div>
    </section>

    <quote-band
      block-id="proposal"
      :title="t('Request a project proposal')"
      :lede="t('Tell us what you need and we come back with every deliverable, the fees plus the delivery deadlines in writing, long before any work starts.')"
      :cta="t('Get in touch')"
      :points="quotePoints"
    />

    <faq-list
      :kicker="t('Before you decide')"
      :title="t('Frequently asked questions')"
      :lede="t('How work is quoted, exactly what you receive, and who owns the finished files once the project is completed and the final invoice is settled.')"
      :items="faqs"
      accordion-id="brand-faq"
    />
  </layout-public>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';


/* Monospace is the tell that a value is a spec rather than copy. Declared once
   and reused by the hex codes, the type scale and the format chips. */

/* ---- 1. Opening argument + the kit sheet ------------------------------- */

.bp-open {
  background: $ground;
}

.bp-open-grid {
  display: grid;
  gap: 2.5rem;
  grid-template-columns: minmax(0, 1fr);

  @media (min-width: 992px) {
    align-items: center;
    gap: 3.5rem;
    grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
  }
}

/* The .bp-actions row is gone: the button owns its own hover and the gap comes
   from the global .action-row. */

/* The sheet reads as a page torn out of a brand manual: graph paper under it,
   two print registration marks on the diagonal, and three labelled boards. */
.bp-sheet {
  background-color: $ground;
  background-image:
    linear-gradient($ground-accent 1px, transparent 1px),
    linear-gradient(90deg, $ground-accent 1px, transparent 1px);
  background-size: 1.5rem 1.5rem;
  border: 1px solid $line-blue;
  border-radius: $r-lg;
  box-shadow: $shadow-band;
  display: grid;
  gap: 1.1rem;
  padding: 1.75rem;
  position: relative;
  /* The sheet is the one object here that reads as a physical page, so it is
     what answers the pointer: a small lift, a deeper shadow, and the two
     registration marks pulling out past the corners the way crop marks are
     placed. Light only - nothing inside the sheet resizes or moves. */
  transition: box-shadow $dur $ease, transform $dur $ease;

  &::before,
  &::after {
    border: 2px solid $primary;
    content: '';
    height: 0.8rem;
    position: absolute;
    transition: transform $dur $ease;
    width: 0.8rem;
  }

  &::before {
    border-bottom: 0;
    border-right: 0;
    left: 0.6rem;
    top: 0.6rem;
  }

  &::after {
    border-left: 0;
    border-top: 0;
    bottom: 0.6rem;
    right: 0.6rem;
  }

  &:hover {
    box-shadow: $shadow-band;
    transform: translateY(-3px);

    &::before {
      transform: translate(-3px, -3px);
    }

    &::after {
      transform: translate(3px, 3px);
    }
  }
}

/* Each board is its own little artboard, labelled the way a manual labels one.

   The padding is 1.5rem rather than the 1.1rem it began at because of the colour
   palette: the deck in that board fans a wing out past its own box when the
   pointer reaches a card, and a wing travels into the board's padding. At 1.1rem
   the outermost card of a fanned deck crossed the border, which reads as a
   rendering fault rather than as a hand of samples. The inset has to be wider
   than the travel rather than equal to it, and SwatchList's own note holds the
   other end of that arithmetic. */
.bp-board {
  background: $ground;
  border: 1px solid $line;
  border-radius: $r-md;
  padding: 1.5rem;
  /* Each artboard answers on its own: a hair of lift, a firmer edge, and its
     label taking the primary, which is the same labelled-tile response the
     rest of the site uses. */
  transition: border-color $dur $ease, box-shadow $dur $ease, transform $dur $ease;

  &:hover {
    border-color: $tint-primary-strong;
    box-shadow: $shadow-lift;
    transform: translateY(-2px);

    .bp-board-label {
      color: $primary;
    }
  }
}

/* The name of a board, over what the board holds.

   It takes the shared $label-* step rather than the four values it used to
   spell. It was 0.66rem at weight 600 with 0.16em of tracking, which is the same
   label the case page's spec sheet sets at $label-size, and the same job on the
   same kind of page: a board on the brand kit and a group on a case study are
   both a named body of content on a light ground. Two sizes for that is the
   drift $label-* was declared to end, and this was the copy that survived it. It
   was also a step under the 4.5:1 a small label needs, because $text-muted
   measures 2.7:1 on white, so the label was reading at the thinnest contrast on
   the page while being the thing that names each board.

   The size and the ink therefore move together here: $label-size and
   $label-on-light are the token's own pair, and the boards repaint a step larger
   and a step darker. That repaint is the point rather than the cost, because it
   is what makes this label and the case page's group head the same label. */
.bp-board-label {
  color: $label-on-light;
  display: block;
  font-family: $headings-font-family;
  font-size: $label-size;
  font-weight: $label-weight;
  letter-spacing: $label-tracking;
  margin-bottom: 0.85rem;
  transition: color $dur $ease;
  text-transform: uppercase;
}

/* Three lockups side by side: the mark on white, the mark reversed out of the
   primary gradient, and the mark alone for tight spaces. */
.bp-lockups {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 575.98px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.bp-lockup {
  align-items: center;
  background: $ground-tint;
  border-radius: $r-md;
  display: flex;
  gap: 0.55rem;
  justify-content: center;
  min-width: 0;
  padding: 0.9rem 0.6rem;
  transition: box-shadow $dur $ease, transform $dur $ease;

  /* The lockups are the three ways the mark is set, so each lifts under the
     pointer like a card picked off a sheet. */
  &:hover {
    box-shadow: $shadow-lift;
    transform: translateY(-3px);
  }

  img {
    display: block;
    flex: 0 0 auto;
    height: 1.75rem;
    width: auto;
  }

  &--inverse {
    background: $grad-deep;

    .bp-wordmark {
      color: $on-dark-strong;
    }
  }

  &--mark {
    background: $ground-dark;
  }
}

.bp-wordmark {
  color: $text-heading;
  font-family: $headings-font-family;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  white-space: nowrap;
}

/* The swatch row is SwatchList's now. It began here, as .bp-swatches, and moved
   out when a portfolio case study needed the same chips: the height, the radius
   and the hover belonged to the object, not to this sheet. */

/* Type specimen: one large glyph for the face, then the steps down as bars
   whose width falls with the size, so the hierarchy is visible at a glance. */
.bp-type {
  align-items: center;
  display: grid;
  gap: 1.25rem;
  grid-template-columns: auto minmax(0, 1fr);

  @media (max-width: 575.98px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.bp-aa {
  color: $text-heading;
  font-family: $headings-font-family;
  font-size: 3.2rem;
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 0.82;
}

.bp-scale {
  display: grid;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    align-items: center;
    display: grid;
    gap: 0.7rem;
    grid-template-columns: minmax(0, 1fr) 1.6rem;

    /* The bar thickens on its own row, so the step you are pointing at reads
       without the row moving or any size changing. */
    &:hover {
      .bp-scale-bar {
        transform: scaleY(1.4);
      }

      .bp-scale-size {
        color: $text-heading;
      }
    }
  }
}

.bp-scale-bar {
  background: linear-gradient(90deg, $primary, $tint-primary-strong);
  border-radius: $r-pill;
  display: block;
  height: 0.4rem;
  transition: transform $dur $ease;
}

.bp-scale-size {
  color: $text-muted;
  font-family: $font-mono;
  font-size: 0.62rem;
  text-align: right;
  transition: color $dur $ease;
}

/* What to send, as three rules-separated items under the sheet. The tile is
   tinted rather than solid primary, so it is not the sixth primary square on
   the page. */
.bp-send-title {
  color: $text-heading;
  font-size: 1.05rem;
  margin: 3.25rem 0 0;
}

.bp-send {
  border-top: 1px solid $line;
  display: grid;
  gap: 1.5rem;
  grid-template-columns: minmax(0, 1fr);
  list-style: none;
  margin: 1.25rem 0 0;
  padding: 1.75rem 0 0;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  li {
    display: grid;
    gap: 0.85rem;
    grid-template-columns: 2.4rem minmax(0, 1fr);

    /* Colour only. The row does not lift, nothing appears behind it, and the
       icon stays put: the response is the title taking the accent and the icon
       tile deepening a step, which reads without moving anything. */
    &:hover,
    &:focus-within {
      .bp-send-icon {
        background: $tint-primary;
      }

      strong {
        color: $primary;
      }
    }
  }

  strong {
    color: $text-heading;
    display: block;
    font-family: $headings-font-family;
    font-size: 1rem;
    margin-bottom: 0.15rem;
    transition: color $dur $ease;
  }

  p {
    color: $text-label;
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 0;
  }
}

.bp-send-icon {
  align-items: center;
  background: $ground-accent;
  border-radius: $r-md;
  color: $primary;
  display: flex;
  height: 2.4rem;
  justify-content: center;
  transition: background-color $dur $ease;
  width: 2.4rem;

  svg {
    height: 1rem;
    width: 1rem;
  }
}

/* ---- 2. The dark surface stage ----------------------------------------- */

/* The section band carries no ground of its own: it falls through to the generic
   `section.dark` rule, which is the neutral grey every dark band on the site uses.
   The wall below supplies all the colour, so the band stays a quiet field. */

/* A mosaic, not six cards. The gap is the wall's own background showing through
   it, which draws every divider in one declaration instead of per-tile borders,
   and the frame is the same colour, so the outer edge is simply the outermost
   grout line and the whole grid reads as one rule.

   The grout is the phone and letterhead ground (#0b2c58), so the dividers sit at
   the deep end of the blue ramp rather than above or below it. It reads as the
   mortar between six pieces of the same material: quiet against the mid and
   primary steps, still clearly a drawn grid against the near-black brochure and
   the bright name card.

   The frame is the last grout line made solid: 3px solid, in the same colour and
   at the same width as the gap, so the outer edge is not a different treatment
   bolted on. Previously the gap was white 22% over the $ground-dark band, which landed on
   a mid grey and read as a fabricated grid on top of the artwork instead of the
   ground showing between it. */
.bp-wall {
  background: #0b2c58;
  border: 3px solid #0b2c58;
  border-radius: $r-lg;
  display: grid;
  gap: 3px;
  grid-auto-rows: 6rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  overflow: hidden;
}

/* Tile grounds are a blue ramp. The illustrations are white on blue, so a neutral
   ground reads as a mismatch: the white glow has nothing to blend into and the
   blue in the artwork looks cut out. Four steps, deepest to brightest, and every
   cell is a step on that ramp.

   The two bands each run deep to bright (deep, mid, primary; deep, mid, blue),
   with the name card as the one bright note. Phone and invoice share the mid
   step, which reads as a rhythm rather than a repeat because they are not
   adjacent. */
.bp-tile {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin: 0;
  padding: 1.1rem;
  /* Every cell in the mosaic answers the pointer the same way: the caption comes
     up to full white and the artwork inside lifts a little. The tile ground never
     changes, so the blue ramp stays exactly as composed.

     There is no rim light on the cell. An inset hairline around a hovered tile
     boxed it in and made each cell read as its own card sitting on the grid, when
     the whole point of the mosaic is that it is one composed piece. The motion
     inside the cell is the whole response. */
  &:hover {
    .bp-tile-art {
      transform: translateY(-5px) scale(1.04);
    }

    .bp-tile-tag {
      color: $on-dark-strong;
    }
  }

  &--brochure {
    background: linear-gradient(165deg, #12396f, #0a2450);
    grid-column: span 2;
    grid-row: span 2;
    position: relative;

    /* One pool of light under the sheet, the way a flat-lay is lit, so the widest
       tile in the wall is a lit surface rather than paper on dead space. It
       brightens on hover, which is what makes the widest tile feel lit rather
       than merely outlined. */
    &::after {
      background: radial-gradient(circle at 50% 62%, rgba($primary, .4), transparent 64%);
      content: '';
      inset: 0;
      pointer-events: none;
      position: absolute;
      transition: filter $dur $ease;
    }

    &:hover::after {
      filter: brightness(1.35);
    }
  }

  &--phone {
    background: #0b2c58;
  }

  &--card {
    background: $grad-deep;
  }

  &--poster {
    background: #0a2450;
    grid-row: span 2;
    position: relative;

    /* The one bright note on the deepest tile. It lights up on hover instead of
       growing, so the rule keeps the exact width it was composed at. */
    &::after {
      background: linear-gradient(90deg, $primary, $accent-blue);
      bottom: 0;
      content: '';
      height: 0.3rem;
      left: 0;
      position: absolute;
      right: 0;
      transition: box-shadow $dur $ease;
    }

    &:hover::after {
      box-shadow: 0 0 1.25rem rgba($accent-blue, .55);
    }
  }

  &--letter {
    background: #0b2c58;
    grid-row: span 2;
  }

  &--profile {
    background: #0d3f86;
    grid-column: span 2;
    grid-row: span 2;
  }
}

/* One span for every tile: two grid rows, so the whole wall is the same height
   and every illustration scales to the same box. The uneven part is the column
   span only, which is what keeps the mosaic composed. When the second band ran
   three rows instead of two, its artwork rendered 1.5x larger than the first
   band's for no reason the eye could see. */
@media (min-width: 768px) {
  .bp-wall {
    grid-auto-rows: 5.75rem;
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }

  .bp-tile {
    &--brochure {
      grid-column: span 6;
      grid-row: span 2;
    }

    &--phone {
      grid-column: span 3;
      grid-row: span 2;
    }

    &--card {
      grid-column: span 3;
      grid-row: span 2;
    }

    &--poster {
      grid-column: span 3;
      grid-row: span 2;
    }

    &--letter {
      grid-column: span 5;
      grid-row: span 2;
    }

    &--profile {
      grid-column: span 4;
      grid-row: span 2;
    }
  }
}

/* Lifted above any tile pseudo-element (the brochure light pool, the poster
   rule), so the artwork is never tinted by the surface behind it. */
.bp-tile-art {
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  min-height: 0;
  position: relative;
  transition: transform $dur $ease;
  z-index: 1;
}

/* The supplied illustration. object-fit: contain rather than cover, because each
   piece has to be shown whole: cropping a name card or an invoice to fill the
   tile would cut the very detail the tile is there to show. The artwork is a
   transparent PNG, so it sits on the tile ground rather than in a box. */
.bp-tile-img {
  display: block;
  filter: drop-shadow(0 0.6rem 1.1rem rgb(0 0 0 / 45%));
  height: auto;
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  width: auto;
}

/* Mono index plus the surface name, bottom-left of every tile. The index is
   what makes the wall read as a spec sheet. */
.bp-tile-tag {
  align-items: center;
  /* Raised from white 62%: the grounds are blue rather than near-black, so the
     caption needs a touch more of the ground's own brightness to hold. */
  color: $on-dark-body;
  display: flex;
  font-family: $headings-font-family;
  font-size: 0.68rem;
  font-weight: 600;
  gap: 0.5rem;
  letter-spacing: 0.16em;
  position: relative;
  /* The bright cells in the ramp leave white type at around 2.4:1, which is
     legible but thin. A short dark shadow lifts the caption off the blue without
     changing the ground, and inherits into the index span with it. */
  text-shadow: 0 1px 0.15rem rgba($ink, .45);
  text-transform: uppercase;
  transition: color $dur $ease;
  z-index: 1;
}

.bp-tile-num {
  color: $dark-accent;
  flex: 0 0 auto;
  font-family: $font-mono;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0;
}

/* The two bright cells take a white index. $dark-accent is a light blue that sits
   too close to the primary to read against it (1.86:1 on the name card), so on
   those grounds the number would be the least legible thing on the tile. */
.bp-tile--card .bp-tile-num,
.bp-tile--profile .bp-tile-num {
  color: $on-dark-strong;
}

/* Three reasons, numbered, on rules, as a bare PanelList: the row rhythm, the
   two-digit mark and the first-row trims are the same ones every other ruled
   list on the site uses. The list still answers the pointer with the title
   colour only, because it is three statements rather than three choices. All
   that is left here is the space between the surface wall and the list. */
.bp-reasons {
  margin-top: 2.75rem;
}

/* ---- 3. The delivery list ---------------------------------------------- */

.bp-manifest {
  background: $ground-tint;
}

/* A ruled spec list. Hairlines between rows do the work the card borders do
   everywhere else, which is what makes this read as a manifest.

   It is a bare, still, numbered, trailing PanelList: the rows keep the shared
   rhythm and the two-digit mark, the file formats ride in the panel's extra
   slot so the copy column takes what is left of the row, and `still` is what
   the owner asked for on this one list. A ruled spec sheet that highlights a
   row reads as a selectable menu, so nothing here answers the pointer. */
.bp-manifest-list {
  /* The sheet is closed by a rule, the way a printed spec sheet is. Rows are
     divided by their own hairlines, so only the last row needs a bottom edge.
     The row still sits on the section ground rather than in a panel box. */
  :deep(.panel-list-item:last-child) {
    border-bottom: 1px solid $line-blue;
  }

  :deep(.panel-list-body) {
    max-width: 38rem;
  }
}

/* File formats drawn as file icons rather than chips. These are literal
   extensions, not copy, so they are never translated. Each one is a page with
   its corner folded, the family mark in the body, and the extension on a tab in
   the family colour: the shape a supplier sees in a folder. */
.bp-formats {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 767.98px) {
    grid-column: 2;
  }

  li {
    --file-tint: #0768dc;
    --file-soft: #e7efff;

    display: inline-flex;

    /* Tinted by what kind of file it is, so a mixed list can be read as groups
       at a glance. The three blue steps sit side by side often (Logo lockups
       ships SVG, PDF and PNG together), which reads as one family rather than
       as three unrelated colours. Two steps per family: the saturated tint for
       the tab and the mark, and a soft wash for the page body, which is what
       lets the sheet read on the pale grey ground now that the outline is gone. */
    &.is-vector {
      --file-tint: #0d6efd;
      --file-soft: #e6efff;
    }

    &.is-raster {
      --file-tint: #00a0ff;
      --file-soft: #e2f4ff;
    }

    &.is-swatch {
      --file-tint: #fd0d6e;
      --file-soft: #ffe6f0;
    }

    &.is-type {
      --file-tint: #7c8798;
      --file-soft: #edf0f4;
    }
  }
}

/* One sheet: page body above, extension tab below, corner cut. Portrait at
   letter ratio. The first pass was wider than it was tall, which is what made it
   read as a swatch card rather than a sheet of paper.

   No outline. A soft wash of the family colour does the separating instead,
   which is the only way the silhouette reads on a $ground-tint ground: plain white on
   near-white is invisible, and a hard rule is what was asked to go. */
.bp-file {
  /* The corner cut, named once. The clip-path references it twice and the folded
     flap takes its own box from it, so three copies of the same literal is
     exactly how the cut and the fold drift apart on the next size change. */
  --file-fold: 0.7rem;

  background: var(--file-soft);
  clip-path: polygon(0 0, calc(100% - var(--file-fold)) 0, 100% var(--file-fold), 100% 100%, 0 100%);
  display: flex;
  flex-direction: column;
  height: 4rem;
  position: relative;
  /* A file icon is a small object, so it is what answers the pointer without
     the row moving: the sheet lifts, the folded corner opens a step, and the
     family mark grows. The lift shadow has to be a drop-shadow rather than a
     box-shadow, because the clip-path above cuts a box-shadow away entirely. */
  transition: filter $dur $ease, transform $dur $ease;
  width: 3.25rem;

  /* The folded flap, lying against the cut diagonal. A step darker than the body
     so the fold still reads once the sheet has no outline. */
  &::after {
    background: var(--file-tint);
    clip-path: polygon(0 0, 100% 100%, 0 100%);
    content: '';
    height: var(--file-fold);
    opacity: 0.22;
    position: absolute;
    right: 0;
    top: 0;
    transition: opacity $dur $ease;
    width: var(--file-fold);
  }

  &:hover {
    filter: drop-shadow(0 0.7rem 1rem rgb(15 40 90 / 26%));
    transform: translateY(-3px);

    &::after {
      opacity: 0.38;
    }

    .bp-file-mark {
      transform: scale(1.15);
    }
  }
}

/* The family mark, centred in the page body above the tab. Auto margins do the
   centring, so the tab keeps its own height and the mark takes what is left. */
.bp-file-mark {
  align-self: center;
  color: var(--file-tint);
  font-size: 1rem;
  margin: auto 0;
  transition: transform $dur $ease;
}

/* The extension tab. The extension is the actual value on the row, so it is the
   one part that must not be small: at the first size it set at 9px, which is
   under where a three-letter extension is comfortable. The sheet is a quarter
   larger and the tab carries 12px, against a tab that grew with it. */
.bp-file-ext {
  background: var(--file-tint);
  color: $on-dark-strong;
  font-family: $font-mono;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1;
  padding: 0.2rem 0.05rem 0.22rem;
  text-align: center;
  text-transform: uppercase;
}

.bp-manifest-note {
  color: $text-label;
  font-size: 0.92rem;
  line-height: 1.6;
  margin: 2.25rem 0 0;
  max-width: 46rem;
}

/* ---- 4. The order sheet ------------------------------------------------ */

.bp-order {
  background: $ground;
}

/* The slip is InkSlab: the same ink slab the promo details pages open their
   brief with and the portfolio case page now carries. It used to be declared
   here, a byte-for-byte copy of `.brief-panel` under a second name. */

/* Two ruled columns of pieces, as a bare PanelList: the same $panel-row-pad
   rhythm, the same 2.1rem outline mark and the same first-row trims as the
   promo brief's ink slab, which is the same sheet. The slab owns the surface
   and the glow, so the list only divides it. */

</style>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  faArrowDown,
  faBezierCurve,
  faCheck,
  faFileLines,
  faFont,
  faImage,
  faListCheck,
  faPalette,
  faStamp
} from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import PageMeta from '@/Components/PageMeta.vue';
import AppButton from '@/Components/AppButton.vue';
import FaqList from '@/Components/FaqList.vue';
import LayoutPublic from '@/Layouts/Public.vue';
import PageHero from '@/Components/PageHero.vue';
import InkSlab from '@/Components/InkSlab.vue';
import PanelList from '@/Components/PanelList.vue';
import QuoteBand from '@/Components/QuoteBand.vue';
import SectionTitle from '@/Components/SectionTitle.vue';
import SwatchList from '@/Components/SwatchList.vue';

FontAwesomeLibrary.add(
  faArrowDown,
  faBezierCurve,
  faCheck,
  faFileLines,
  faFont,
  faImage,
  faListCheck,
  faPalette,
  faStamp
);

/* File-extension families. Drives the tint of the page glyph on each format
   chip so a mixed list groups by kind of file. An extension with no entry
   falls back to the print tint. */
const FORMAT_FAMILY = {
  AI: 'vector',
  ASE: 'swatch',
  EPS: 'vector',
  FIG: 'vector',
  HEX: 'swatch',
  JPG: 'raster',
  OTF: 'type',
  PDF: 'print',
  PNG: 'raster',
  PSD: 'raster',
  SVG: 'vector',
  TTF: 'type'
};

function formatFamily(format) {
  return FORMAT_FAMILY[String(format).toUpperCase()] || 'print';
}

/* The mark drawn in the body of each file icon, keyed by family rather than by
   extension: a page and a poster share the vector mark, because the family is
   what the icon is saying. */
const FORMAT_ICON = {
  vector: 'bezier-curve',
  swatch: 'palette',
  raster: 'image',
  type: 'font',
  print: 'file-lines'
};

function formatIcon(format) {
  return FORMAT_ICON[formatFamily(format)] || 'file-lines';
}

export default {
  components: {
    AppButton,
    FaqList,
    PageMeta,
    LayoutPublic,
    PageHero,
    InkSlab,
    PanelList,
    QuoteBand,
    SectionTitle,
    SwatchList
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'A brand kit is a one-time cost that every flyer, slide and post after it reuses. It also stops the slow drip of small fixes that make a business look smaller than it is.': 'A brand kit is a one-time cost that every flyer, slide and post after it reuses. It also stops the slow drip of small fixes that make a business look smaller than it is.',
          'A complete logo and colour palette, with every single working file that your team, your printer and your website builder will ever ask you for.': 'A complete logo and colour palette, with every single working file that your team, your printer and your website builder will ever ask you for.',
          'A written proposal with the fee and the delivery date. No payment until you approve it.': 'A written proposal with the fee and the delivery date. No payment until you approve it.',
          'A logo to sharpen': 'A logo to sharpen',
          'Brochures and company profiles': 'Brochures and company profiles',
          'Before they read a word.': 'Before they read a word.',
          'Beyond the kit, we produce the individual branded pieces when you need them, each piece quoted separately so nothing is ever bundled together.': 'Beyond the kit, we produce the individual branded pieces when you need them, each piece quoted separately so nothing is ever bundled together.',
          'Company profile': 'Company profile',
          'Folded brochures and multi-page company profiles, laid out on the same grid as the kit.': 'Folded brochures and multi-page company profiles, laid out on the same grid as the kit.',
          'Buyers decide in the first three seconds': 'Buyers decide in the first **three seconds**',
          'Can extra languages go on the website later?': 'Can extra languages go on the website later?',
          'Can you copy a look from another company?': 'Can you copy a look from another company?',
          'Can you sign an NDA before I send content?': 'Can you sign an NDA before I send content?',
          'Can you use a logo I already have?': 'Can you use a logo I already have?',
          'Colour palette': 'Colour palette',
          'Do I own the files when it is done?': 'Do I own the files when it is done?',
          'Editable post and cover layouts so every week looks like it came from the same company.': 'Editable post and cover layouts so every week looks like it came from the same company.',
          'Every flyer, slide and sticker is built from the same kit instead of being designed again from scratch.': 'Every flyer, slide and sticker is built from the same kit instead of being designed again from scratch.',
          'One spelling, and it reads the same on the site, the brochure and the name card.': 'One spelling, and it reads the same on the site, the brochure and the name card.',
          'Files and rights': 'Files and rights',
          'How long does it take?': 'How long does it take?',
          'How much does a logo or brand kit cost?': 'How much does a logo or brand kit cost?',
          'How work is quoted, exactly what you receive, and who owns the finished files once the project is completed and the final invoice is settled.': 'How work is quoted, exactly what you receive, and who owns the finished files once the project is completed and the final invoice is settled.',
          'Invoice and letterhead': 'Invoice and letterhead',
          'It survives a change of supplier': 'It survives a change of supplier',
          'Menu and product catalog layouts, prepared for the printer you already work with.': 'Menu and product catalog layouts, prepared for the printer you already work with.',
          'Logo lockups': 'Logo lockups',
          'Brand Presence': 'Brand Presence',
          'Main and support colours with the codes printers and screens ask for, so the blue stays the same blue.': 'Main and support colours with the codes printers and screens ask for, so the blue stays the same blue.',
          'Name card': 'Name card',
          'Name cards and letterheads': 'Name cards and letterheads',
          'Name cards, letterhead and an invoice layout your team can print or send the same day.': 'Name cards, letterhead and an invoice layout your team can print or send the same day.',
          'Next to the website': 'Next to the website',
          'Never simply a picture of a logo. These are the working files which your printer, your website builder and your social templates ask you for.': 'Never simply a picture of a logo. These are the working files which your printer, your website builder and your social templates ask you for.',
          'One kit, every surface.': 'One **kit**, every surface.',
          'One look across every channel': 'One look across every channel',
          'Order only the pieces you need': 'Order **only** the pieces you need',
          'Menus and catalogs': 'Menus and catalogs',
          'Phone and profile picture': 'Phone and profile picture',
          'Flyer and poster': 'Flyer and poster',
          'Print and screen files': 'Print and screen files',
          'Print-ready files with bleed, colour codes and outlined fonts, so a job does not come back wrong.': 'Print-ready files with bleed, colour codes and outlined fonts, so a job does not come back wrong.',
          'Printed from the same kit, so a stack of cards matches the site and the brochure.': 'Printed from the same kit, so a stack of cards matches the site and the brochure.',
          'Reusable post layouts, profile art and cover images you can edit yourself.': 'Reusable post layouts, profile art and cover images you can edit yourself.',
          'See the kit': 'See the kit',
          'Send a photo or a hex code. We build the palette around the colours you already use.': 'Send a photo or a hex code. We build the palette around the colours you already use.',
          'Brochure': 'Brochure',
          'Social templates': 'Social templates',
          'Social templates and covers': 'Social templates and covers',
          'Source files are handed over. A new designer or printer picks up without redrawing anything.': 'Source files are handed over. A new designer or printer picks up without redrawing anything.',
          'Stationery set': 'Stationery set',
          'SVG, PDF, PNG and JPG, with nothing to redraw, stretch or trace when a supplier asks.': 'SVG, PDF, PNG and JPG, with nothing to redraw, stretch or trace when a supplier asks.',
          'Tell us what you need and we come back with every deliverable, the fees plus the delivery deadlines in writing, long before any work starts.': 'Tell us what you need and we come back with every deliverable, the fees plus the delivery deadlines in writing, long before any work starts.',
          'Your name, done right': 'Your name, done right',
          'The delivery sheet': 'The delivery sheet',
          'The final artwork and the source files are yours at handover, for print and screen.': 'The final artwork and the source files are yours at handover, for print and screen.',
          'The pieces, the sizes, and whether you need print-ready files or screen files.': 'The pieces, the sizes, and whether you need print-ready files or screen files.',
          'The brochure, the name card, the website and the social profile picture should never look like they came from four completely different companies.': 'The brochure, the name card, the website and the social profile picture should never look like they came from four completely different companies.',
          'Those three seconds are your logo, your colours and your spacing. Done well, a young company instantly reads as an established, trusted one.': 'Those three seconds are your logo, your colours and your spacing. Done well, a young company instantly reads as an established, trusted one.',
          'Colours you already like': 'Colours you already like',
          'Type and hierarchy': 'Type and hierarchy',
          'A rough file is enough. We keep what reads well and fix the parts that do not.': 'A rough file is enough. We keep what reads well and fix the parts that do not.',
          'What if I only have colours, not a brand kit?': 'What if I only have colours, not a brand kit?',
          'What comes back': 'What comes back',
          'What lands in your hands': 'What lands in your hands',
          'What to send': 'What to send',
          'Bring what you have': 'Bring what you have',
          'Which font for headings, which for body text, and how sizes step down so a page stays readable.': 'Which font for headings, which for body text, and how sizes step down so a page stays readable.',
          'You stop paying for look-alikes': 'You stop paying for look-alikes',
          'Your name set the ways you need it, plus a small mark for tight spaces like a favicon or a profile picture.': 'Your name set the ways you need it, plus a small mark for tight spaces like a favicon or a profile picture.',
          'Your printer stops asking questions': 'Your printer stops asking questions',
          'faq-clone': 'We can use it as a reference for layout and sections. We do not clone the branding, artwork or copy of another company.',
          'faq-colours': 'That is enough to start a website. Send two colours, the company name, and photos if you have them. A full brand kit is a separate quote.',
          'faq-cost': 'It depends on how many lockups, concepts and pieces you need. Send a short brief and we reply with a written fee. A single mark for a small business costs far less than a full kit with stationery and templates.',
          'faq-existing': 'Yes. Send the files you have, ideally SVG, AI, EPS or PDF. If you only have a JPG or a screenshot, we redraw it cleanly as part of the job.',
          'faq-languages': 'Yes. Any language is fine. Extra languages add a fee based on how much copy we translate and maintain. Tell us the languages before we quote.',
          'faq-logo': 'Logo design is not inside web design plan fees. We can launch with a wordmark and your colours. A logo is quoted separately.',
          'faq-nda': 'Yes. Send yours or ask for ours. We still need enough outline to quote. An NDA does not replace a proposal.',
          'faq-own': 'Yes. The final artwork and the source files are yours at handover, with no licence fee to keep using your own logo.',
          'faq-time': 'A single mark is usually one to two weeks. A full kit with stationery and templates takes three to five weeks, and we confirm the dates in the proposal.'
        },
        'zh-MY': {
          'A brand kit is a one-time cost that every flyer, slide and post after it reuses. It also stops the slow drip of small fixes that make a business look smaller than it is.': '品牌素材是一次性费用，之后的每张传单、简报与贴文都能沿用。它也能止住那些让公司显得比实际规模更小的零散修补。',
          'A complete logo and colour palette, with every single working file that your team, your printer and your website builder will ever ask you for.': '一套完整的标志与配色，连同您的团队、印刷厂和网站开发真正会用到、并且频繁索取的全部工作文件，一次交付齐全，之后追加也不用重新设计。',
          'A written proposal with the fee and the delivery date. No payment until you approve it.': '载明费用与交付日期的书面提案。您确认之前无需付款。',
          'A logo to sharpen': '把标志打磨好',
          'Brochures and company profiles': '宣传册与公司简介',
          'Before they read a word.': '在他们读到任何文字之前。',
          'Beyond the kit, we produce the individual branded pieces when you need them, each piece quoted separately so nothing is ever bundled together.': '除了整套素材，我们也按需要单件制作。每件独立报价，您不会因为要一本宣传册，而被迫付费做一整套用不上的文具，需要什么才做什么，绝不多收。',
          'Company profile': '公司简介',
          'Folded brochures and multi-page company profiles, laid out on the same grid as the kit.': '折页宣传册与多页公司简介，沿用与整套素材相同的网格排版。',
          'Buyers decide in the first three seconds': '买家在**三秒**内就下了判断',
          'Can extra languages go on the website later?': '之后可以再为网站增加语言吗？',
          'Can you copy a look from another company?': '可以参考其他公司的外观吗？',
          'Can you sign an NDA before I send content?': '在我提供内容前可以签保密协议吗？',
          'Can you use a logo I already have?': '可以使用我现有的标志吗？',
          'Colour palette': '配色方案',
          'Do I own the files when it is done?': '完成后文件归我所有吗？',
          'Editable post and cover layouts so every week looks like it came from the same company.': '可自行编辑的贴文与封面版面，让每周内容看起来都出自同一家公司。',
          'Every flyer, slide and sticker is built from the same kit instead of being designed again from scratch.': '每张传单、简报与贴纸都用同一套素材制作，不必每次从头再设计。',
          'One spelling, and it reads the same on the site, the brochure and the name card.': '一个拼法，让它在网站、宣传册与名片上都一致。',
          'Files and rights': '文件与权利',
          'How long does it take?': '需要多久？',
          'How much does a logo or brand kit cost?': '标志或品牌素材要多少钱？',
          'How work is quoted, exactly what you receive, and who owns the finished files once the project is completed and the final invoice is settled.': '报价方式、您实际会收到什么，以及专案完成、尾款结清之后这些成品文件到底归谁所有，全部一次说清楚，绝不让您事后再多问一句，省时省心。',
          'Invoice and letterhead': '发票与信笺',
          'It survives a change of supplier': '换供应商也不受影响',
          'Menu and product catalog layouts, prepared for the printer you already work with.': '按您现有印刷厂的要求制作菜单与产品目录版式。',
          'Logo lockups': '标志组合',
          'Brand Presence': '品牌形象',
          'Main and support colours with the codes printers and screens ask for, so the blue stays the same blue.': '主色与辅助色，附上印刷与屏幕所需的色值，让同一个蓝色始终一致。',
          'Name card': '名片',
          'Name cards and letterheads': '名片与信笺',
          'Name cards, letterhead and an invoice layout your team can print or send the same day.': '名片、信笺与发票版面，团队当天就能印刷或发送。',
          'Next to the website': '与网站并行',
          'Never simply a picture of a logo. These are the working files which your printer, your website builder and your social templates ask you for.': '不只是标志的一张图片，而是印刷厂、网站开发与社交媒体模板都会开口索取的工作文件，格式齐全，随拿随用，不必再回头找设计师，让谁都能接手。',
          'One kit, every surface.': '一套**素材**，各种场合。',
          'One look across every channel': '各个渠道外观一致',
          'Order only the pieces you need': '**只做**您真正需要的那几样',
          'Menus and catalogs': '菜单与产品目录',
          'Phone and profile picture': '手机与头像',
          'Flyer and poster': '传单与海报',
          'Print and screen files': '印刷与屏幕文件',
          'Print-ready files with bleed, colour codes and outlined fonts, so a job does not come back wrong.': '含出血、色值与字体外框的印刷稿，印出来不会出问题。',
          'Printed from the same kit, so a stack of cards matches the site and the brochure.': '用同一套素材印制，整叠名片与网站、宣传册保持一致。',
          'Reusable post layouts, profile art and cover images you can edit yourself.': '可重复使用的贴文版面、头像图与封面图，您可自行编辑。',
          'See the kit': '查看内容',
          'Send a photo or a hex code. We build the palette around the colours you already use.': '一张照片或一个色码就够。我们围绕您现有的颜色建立配色。',
          'Brochure': '宣传册',
          'Social templates': '社交模板',
          'Social templates and covers': '社交模板与封面',
          'Source files are handed over. A new designer or printer picks up without redrawing anything.': '源文件已交付，新的设计师或印刷厂接手时无需重画任何内容。',
          'Stationery set': '文具系列',
          'SVG, PDF, PNG and JPG, with nothing to redraw, stretch or trace when a supplier asks.': 'SVG、PDF、PNG 与 JPG，供应商索取时无需重画、拉伸或描摹。',
          'Tell us what you need and we come back with every deliverable, the fees plus the delivery deadlines in writing, long before any work starts.': '告诉我们您的需求。开工之前，我们会以书面回复列出需要的件数、费用与交付日期，先谈清楚才动工，不会有中途加价的情况，白纸黑字最实在。',
          'Your name, done right': '把名称写对',
          'The delivery sheet': '交付清单',
          'The final artwork and the source files are yours at handover, for print and screen.': '交付时最终图稿与源文件归您所有，可用于印刷与屏幕。',
          'The pieces, the sizes, and whether you need print-ready files or screen files.': '需要哪些件、尺寸，以及要印刷稿还是屏幕稿。',
          'The brochure, the name card, the website and the social profile picture should never look like they came from four completely different companies.': '宣传册、名片、网站与社交媒体头像，都不该看起来像来自四家完全不同的公司。一致的视觉，是买家愿意相信您的第一步，也是品牌最基本的底气。',
          'Those three seconds are your logo, your colours and your spacing. Done well, a young company instantly reads as an established, trusted one.': '买家在头三秒里看到的，就是您的标志、配色与间距。做得好，年轻公司也能读出成熟企业的份量；做得不好，买家悄悄转投他人，永远不会告诉您原因。',
          'Colours you already like': '您本来就喜欢的颜色',
          'Type and hierarchy': '字体与层级',
          'A rough file is enough. We keep what reads well and fix the parts that do not.': '一份粗略文件就够。我们保留辨识度好的部分，修好不足的地方。',
          'What if I only have colours, not a brand kit?': '如果我只有颜色、没有品牌手册呢？',
          'What comes back': '我们回传什么',
          'What lands in your hands': '您实际会**拿到什么**',
          'What to send': '需要提供什么',
          'Bring what you have': '把您现有的带来',
          'Which font for headings, which for body text, and how sizes step down so a page stays readable.': '标明标题与内文各用哪种字体，以及字号如何递减，让版面保持易读。',
          'You stop paying for look-alikes': '不必重复付费做相似的东西',
          'Your name set the ways you need it, plus a small mark for tight spaces like a favicon or a profile picture.': '按您实际需要排好的名称组合，另有适用于小空间（如网站图标或头像）的简版标志。',
          'Your printer stops asking questions': '印刷厂不再反复追问',
          'faq-clone': '我们可以把它当作版面与区块的参考。我们不会复制其他公司的品牌、图稿或文案。',
          'faq-colours': '这就足以开始网站。请提供两种颜色、公司名称，以及现有照片。完整品牌手册另行报价。',
          'faq-cost': '费用取决于需要多少个标志组合、方案与件数。请提供简短需求，我们会以书面回复报价。小型企业只做一个标志，费用远低于包含文具与模板的整套品牌素材。',
          'faq-existing': '可以。请提供现有文件，最好是 SVG、AI、EPS 或 PDF。若只有 JPG 或截图，我们会在工作范围内重新绘制干净版本。',
          'faq-languages': '可以。任何语言都可以。额外语言会按需翻译与维护的文案量加收费用。报价前请告知语言。',
          'faq-logo': '标志设计不包含在网页设计配套费用内。我们可以用文字标志与您的颜色先上线。标志另行报价。',
          'faq-nda': '可以。请提供您的版本，或向我们索取。我们仍需要足够大纲才能报价。保密协议不能取代提案。',
          'faq-own': '是的。交付时最终图稿与源文件归您所有，继续使用自己的标志无需额外授权费。',
          'faq-time': '单一标志通常一至两周。含文具与模板的整套品牌素材为三至五周，具体日期会在提案中确认。'
        },
        'ms-MY': {
          'A brand kit is a one-time cost that every flyer, slide and post after it reuses. It also stops the slow drip of small fixes that make a business look smaller than it is.': 'Kit jenama ialah kos sekali sahaja yang digunakan semula oleh setiap risalah, slaid dan siaran selepasnya. Ia juga menghentikan pembaikan kecil yang berterusan dan membuat perniagaan kelihatan lebih kecil daripada hakikatnya.',
          'A complete logo and colour palette, with every single working file that your team, your printer and your website builder will ever ask you for.': 'Logo, palet warna yang lengkap dan semua fail kerja yang memastikan setiap titik sentuhan jenama anda kekal konsisten, daripada cetakan sampai ke web.',
          'A written proposal with the fee and the delivery date. No payment until you approve it.': 'Cadangan bertulis dengan yuran dan tarikh siap. Tiada bayaran sehingga anda meluluskannya.',
          'A logo to sharpen': 'Logo untuk diperhalus',
          'Brochures and company profiles': 'Risalah dan profil syarikat',
          'Before they read a word.': 'Sebelum mereka membaca satu perkataan.',
          'Beyond the kit, we produce the individual branded pieces when you need them, each piece quoted separately so nothing is ever bundled together.': 'Selain kit, kami buat bahan individu mengikut keperluan anda. Setiap satu disebut harga sendiri, jadi satu risalah tidak memaksa anda bayar set penuh.',
          'Company profile': 'Profil syarikat',
          'Folded brochures and multi-page company profiles, laid out on the same grid as the kit.': 'Risalah lipat dan profil syarikat berbilang halaman, disusun pada grid yang sama dengan kit.',
          'Buyers decide in the first three seconds': 'Pembeli menilai dalam **tiga saat** pertama',
          'Can extra languages go on the website later?': 'Boleh bahasa tambahan ditambah pada laman web kemudian?',
          'Can you copy a look from another company?': 'Boleh anda meniru rupa syarikat lain?',
          'Can you sign an NDA before I send content?': 'Boleh anda menandatangani NDA sebelum saya hantar kandungan?',
          'Can you use a logo I already have?': 'Boleh anda guna logo yang saya sudah ada?',
          'Do I own the files when it is done?': 'Adakah fail itu milik saya apabila siap?',
          'Editable post and cover layouts so every week looks like it came from the same company.': 'Susun atur siaran dan kulit yang boleh diedit supaya setiap minggu kelihatan dari syarikat yang sama.',
          'Every flyer, slide and sticker is built from the same kit instead of being designed again from scratch.': 'Setiap risalah, slaid dan pelekat dibina daripada kit yang sama, bukan direka semula dari awal.',
          'One spelling, and it reads the same on the site, the brochure and the name card.': 'Satu ejaan, dan ia konsisten pada laman web, risalah dan kad nama.',
          'Files and rights': 'Fail dan hak',
          'How long does it take?': 'Berapa lama ia mengambil masa?',
          'How much does a logo or brand kit cost?': 'Berapa kos logo atau kit jenama?',
          'How work is quoted, exactly what you receive, and who owns the finished files once the project is completed and the final invoice is settled.': 'Bagaimana kerja disebut harga, apa yang anda terima, dan siapa yang memiliki fail siap itu apabila projek tamat dan bayaran penuh sudah dijelaskan.',
          'Invoice and letterhead': 'Invois dan kepala surat',
          'It survives a change of supplier': 'Ia kekal walaupun tukar pembekal',
          'Menu and product catalog layouts, prepared for the printer you already work with.': 'Susun atur menu dan katalog produk, disediakan untuk pencetak yang anda sudah gunakan.',
          'Logo lockups': 'Gandingan logo',
          'Brand Presence': 'Kehadiran Jenama',
          'Main and support colours with the codes printers and screens ask for, so the blue stays the same blue.': 'Warna utama dan sokongan dengan kod yang diminta pencetak dan skrin, supaya biru itu kekal biru yang sama.',
          'Name card': 'Kad nama',
          'Name cards and letterheads': 'Kad nama dan kepala surat',
          'Name cards, letterhead and an invoice layout your team can print or send the same day.': 'Kad nama, kepala surat dan susun atur invois yang boleh dicetak atau dihantar oleh pasukan anda pada hari yang sama.',
          'Next to the website': 'Di sisi laman web',
          'Never simply a picture of a logo. These are the working files which your printer, your website builder and your social templates ask you for.': 'Bukan sekadar gambar logo. Ini fail kerja sebenar yang diminta oleh pencetak, pembina laman web dan templat media sosial anda. Sedia digunakan.',
          'One kit, every surface.': 'Satu **kit**, semua permukaan.',
          'One look across every channel': 'Satu rupa pada setiap saluran',
          'Order only the pieces you need': 'Tempah **hanya** bahan yang anda perlu',
          'Menus and catalogs': 'Menu dan katalog',
          'Phone and profile picture': 'Telefon dan gambar profil',
          'Flyer and poster': 'Selebaran dan poster',
          'Print and screen files': 'Fail cetak dan skrin',
          'Print-ready files with bleed, colour codes and outlined fonts, so a job does not come back wrong.': 'Fail sedia cetak dengan bleed, kod warna dan huruf bergaris luar, supaya kerja tidak kembali salah.',
          'Printed from the same kit, so a stack of cards matches the site and the brochure.': 'Dicetak daripada kit yang sama, jadi sekumpulan kad sepadan dengan laman web dan risalah.',
          'Reusable post layouts, profile art and cover images you can edit yourself.': 'Susun atur siaran, seni profil dan imej kulit yang boleh anda edit sendiri.',
          'See the kit': 'Lihat kit',
          'Send a photo or a hex code. We build the palette around the colours you already use.': 'Hantar foto atau kod heks. Kami bina palet berdasarkan warna yang anda sudah guna.',
          'Brochure': 'Risalah',
          'Social templates': 'Templat sosial',
          'Social templates and covers': 'Templat sosial dan kulit',
          'Source files are handed over. A new designer or printer picks up without redrawing anything.': 'Fail sumber diserahkan. Pereka atau pencetak baharu boleh menyambung tanpa melukis semula apa-apa.',
          'Stationery set': 'Set alat tulis',
          'SVG, PDF, PNG and JPG, with nothing to redraw, stretch or trace when a supplier asks.': 'SVG, PDF, PNG dan JPG, tanpa perlu dilukis semula, diregang atau dikesan apabila pembekal meminta.',
          'Tell us what you need and we come back with every deliverable, the fees plus the delivery deadlines in writing, long before any work starts.': 'Beritahu kami keperluan anda. Kami balas secara bertulis dengan senarai bahan, yuran dan tarikh siap, sebelum apa-apa bermula. Tanpa kejutan.',
          'Your name, done right': 'Nama anda, ditulis betul',
          'The delivery sheet': 'Senarai serahan',
          'The final artwork and the source files are yours at handover, for print and screen.': 'Karya seni akhir dan fail sumber menjadi milik anda semasa penyerahan, untuk cetak dan skrin.',
          'The pieces, the sizes, and whether you need print-ready files or screen files.': 'Bahan, saiz, dan sama ada anda perlukan fail sedia cetak atau fail skrin.',
          'The brochure, the name card, the website and the social profile picture should never look like they came from four completely different companies.': 'Risalah, kad nama, laman web dan gambar profil tidak sepatutnya kelihatan seperti empat buah syarikat yang berbeza. Ini yang bina keyakinan.',
          'Those three seconds are your logo, your colours and your spacing. Done well, a young company instantly reads as an established, trusted one.': 'Tiga saat itu ialah logo, warna dan jarak reka letak anda. Jika betul, syarikat baharu kelihatan mapan; jika tidak, pembeli memilih orang lain.',
          'Colours you already like': 'Warna yang anda sudah suka',
          'Type and hierarchy': 'Jenis huruf dan hierarki',
          'A rough file is enough. We keep what reads well and fix the parts that do not.': 'Fail kasar sudah cukup. Kami kekalkan yang jelas dan perbaiki yang kurang.',
          'What if I only have colours, not a brand kit?': 'Bagaimana jika saya hanya ada warna, bukan kit jenama?',
          'What comes back': 'Apa yang anda terima',
          'What lands in your hands': 'Apa yang anda sebenarnya terima',
          'What to send': 'Apa yang perlu dihantar',
          'Bring what you have': 'Bawa apa yang anda ada',
          'Which font for headings, which for body text, and how sizes step down so a page stays readable.': 'Huruf mana untuk tajuk, mana untuk teks isi, dan bagaimana saiz menurun supaya halaman kekal mudah dibaca.',
          'You stop paying for look-alikes': 'Anda berhenti bayar untuk kerja berulang',
          'Your name set the ways you need it, plus a small mark for tight spaces like a favicon or a profile picture.': 'Nama anda disusun mengikut keperluan, serta tanda kecil untuk ruang sempit seperti favicon atau gambar profil.',
          'Your printer stops asking questions': 'Pencetak anda berhenti bertanya',
          'faq-clone': 'Kami boleh gunakannya sebagai rujukan untuk reka letak dan bahagian. Kami tidak meniru penjenamaan, karya seni atau salinan syarikat lain.',
          'faq-colours': 'Itu sudah cukup untuk memulakan laman web. Hantar dua warna, nama syarikat, dan foto jika ada. Kit jenama penuh ialah sebut harga berasingan.',
          'faq-cost': 'Ia bergantung pada berapa banyak gandingan, konsep dan bahan yang anda perlukan. Hantar ringkasan ringkas dan kami balas dengan yuran bertulis. Satu tanda untuk perniagaan kecil jauh lebih murah daripada kit penuh dengan alat tulis dan templat.',
          'faq-existing': 'Boleh. Hantar fail yang anda ada, sebaiknya SVG, AI, EPS atau PDF. Jika anda hanya ada JPG atau tangkapan skrin, kami lukis semula dengan bersih sebagai sebahagian daripada kerja itu.',
          'faq-languages': 'Ya. Mana-mana bahasa boleh. Bahasa tambahan menambah yuran berdasarkan berapa banyak salinan yang kami terjemah dan kekalkan. Beritahu kami bahasa sebelum kami sebut harga.',
          'faq-logo': 'Reka bentuk logo tidak termasuk dalam yuran pelan reka bentuk web. Kami boleh lancarkan dengan wordmark dan warna anda. Logo disebut harga secara berasingan.',
          'faq-nda': 'Ya. Hantar milik anda atau minta milik kami. Kami masih perlukan kerangka yang cukup untuk sebut harga. NDA tidak menggantikan cadangan.',
          'faq-own': 'Ya. Karya seni akhir dan fail sumber menjadi milik anda semasa penyerahan, tanpa yuran lesen untuk terus menggunakan logo anda sendiri.',
          'faq-time': 'Satu tanda biasanya satu hingga dua minggu. Kit penuh dengan alat tulis dan templat mengambil masa tiga hingga lima minggu, dan tarikhnya kami sahkan dalam cadangan.'
        }
      }
    });

    /* The palette on the kit sheet. A swatch is a colour chip with its code
       under it, so the code is page copy, and this file cannot read a SCSS
       variable: the top four values are the same four the token file declares
       as $primary, $accent-blue, $brand-deep and $secondary, and the last two
       are $ground-dark and $ground-tint. A change to the brand means changing
       both places. */
    const palette = [
      { hex: '#0D6EFD' },
      { hex: '#00A0FF' },
      { hex: '#0768DC' },
      { hex: '#FD0D6E' },
      { hex: '#222222' },
      { hex: '#F4F7FB' }
    ];

    /* The steps a page reads in, drawn as bars. Sizes only, so the specimen
       needs no translating. */
    const typeScale = [
      { size: '48', width: 100 },
      { size: '32', width: 78 },
      { size: '24', width: 60 },
      { size: '18', width: 44 },
      { size: '14', width: 32 }
    ];

    /* The six surfaces, in the order they appear on the wall. `mod` picks the
       tile variant, which owns the ground and the grid span: the spans are what
       make the mosaic composed rather than even, so they stay in the stylesheet
       where a layout change belongs. */
    const surfaces = computed(() => [
      { mod: 'brochure', src: '/images/services/brand-presence/brochure.webp', label: t('Brochure') },
      { mod: 'phone', src: '/images/services/brand-presence/phone-and-profile-picture.webp', label: t('Phone and profile picture') },
      { mod: 'card', src: '/images/services/brand-presence/name-card.webp', label: t('Name card') },
      { mod: 'poster', src: '/images/services/brand-presence/flyer-and-poster.webp', label: t('Flyer and poster') },
      { mod: 'letter', src: '/images/services/brand-presence/invoice-and-letterhead.webp', label: t('Invoice and letterhead') },
      { mod: 'profile', src: '/images/services/brand-presence/company-profile.webp', label: t('Company profile') }
    ]);

    const logoNeeds = computed(() => [
      { icon: 'font', title: t('Your name, done right'), body: t('One spelling, and it reads the same on the site, the brochure and the name card.') },
      { icon: 'palette', title: t('Colours you already like'), body: t('Send a photo or a hex code. We build the palette around the colours you already use.') },
      { icon: 'image', title: t('A logo to sharpen'), body: t('A rough file is enough. We keep what reads well and fix the parts that do not.') }
    ]);

    /* `formats` are literal file extensions, never translated. They are what the
       delivery list prints as chips on each row. */
    const kitItems = computed(() => [
      { formats: ['SVG', 'PDF', 'PNG'], title: t('Logo lockups'), body: t('Your name set the ways you need it, plus a small mark for tight spaces like a favicon or a profile picture.') },
      { formats: ['ASE', 'HEX', 'PDF'], title: t('Colour palette'), body: t('Main and support colours with the codes printers and screens ask for, so the blue stays the same blue.') },
      { formats: ['OTF', 'PDF'], title: t('Type and hierarchy'), body: t('Which font for headings, which for body text, and how sizes step down so a page stays readable.') },
      { formats: ['PDF', 'PNG', 'JPG'], title: t('Print and screen files'), body: t('SVG, PDF, PNG and JPG, with nothing to redraw, stretch or trace when a supplier asks.') },
      { formats: ['PDF', 'AI'], title: t('Stationery set'), body: t('Name cards, letterhead and an invoice layout your team can print or send the same day.') },
      { formats: ['PSD', 'FIG'], title: t('Social templates'), body: t('Editable post and cover layouts so every week looks like it came from the same company.') }
    ]);

    const brandWhy = computed(() => [
      { title: t('You stop paying for look-alikes'), body: t('Every flyer, slide and sticker is built from the same kit instead of being designed again from scratch.') },
      { title: t('Your printer stops asking questions'), body: t('Print-ready files with bleed, colour codes and outlined fonts, so a job does not come back wrong.') },
      { title: t('It survives a change of supplier'), body: t('Source files are handed over. A new designer or printer picks up without redrawing anything.') }
    ]);

    /* The pieces beyond the kit. No icons: on an order sheet the tick box is the
       only mark a row needs, and a glyph beside it would compete with it. */
    const graphicPieces = computed(() => [
      { title: t('Name cards and letterheads'), body: t('Printed from the same kit, so a stack of cards matches the site and the brochure.') },
      { title: t('Social templates and covers'), body: t('Reusable post layouts, profile art and cover images you can edit yourself.') },
      { title: t('Brochures and company profiles'), body: t('Folded brochures and multi-page company profiles, laid out on the same grid as the kit.') },
      { title: t('Menus and catalogs'), body: t('Menu and product catalog layouts, prepared for the printer you already work with.') }
    ]);

    const quotePoints = computed(() => [
      { icon: 'list-check', title: t('What to send'), body: t('The pieces, the sizes, and whether you need print-ready files or screen files.') },
      { icon: 'file-lines', title: t('What comes back'), body: t('A written proposal with the fee and the delivery date. No payment until you approve it.') },
      { icon: 'stamp', title: t('Files and rights'), body: t('The final artwork and the source files are yours at handover, for print and screen.') }
    ]);

    const faqs = computed(() => [
      { q: t('How much does a logo or brand kit cost?'), a: t('faq-cost') },
      { q: t('I do not have a logo. Do you design one?'), a: t('faq-logo') },
      { q: t('Can you use a logo I already have?'), a: t('faq-existing') },
      { q: t('Do I own the files when it is done?'), a: t('faq-own') },
      { q: t('What if I only have colours, not a brand kit?'), a: t('faq-colours') },
      { q: t('Can you copy a look from another company?'), a: t('faq-clone') },
      { q: t('Can extra languages go on the website later?'), a: t('faq-languages') },
      { q: t('How long does it take?'), a: t('faq-time') },
      { q: t('Can you sign an NDA before I send content?'), a: t('faq-nda') }
    ]);

    return { t, formatFamily, formatIcon, palette, typeScale, surfaces, faqs, brandWhy, graphicPieces, kitItems, logoNeeds, quotePoints };
  }
};
</script>
